import React, { forwardRef, HTMLAttributes } from 'react';
import {
  ScrollableWrapper,
  ScrollableItemsWrapper,
  ScrollBack,
  ScrollForward,
} from '#components/tabs/scroll-buttons.tsx';
import { TabListContextProvider } from '#components/tabs/tablist-context.tsx';
import { useTabsScroll } from '#components/tabs/use-tabs-scroll.tsx';
import { styled } from '../../theme';
import { GDSCustomizableComponent, GDSResponsiveProp } from '../../types';
import { useComposedRefs } from '../../hooks/use-composed-ref';
import { useResponsiveProp } from '../../hooks/use-responsive-prop';
import { Link } from './nav-link';
import { RouterLink } from './router-link';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

export interface GDSNavListProps
  extends GDSCustomizableComponent,
    HTMLAttributes<HTMLElement> {
  divider?: boolean;
  tabDistribution?: GDSResponsiveProp<'distributeEvenly' | 'hugContents'>;
  children?: React.ReactNode;
}

const DISPLAY_NAME = 'NavList';

const BaseNav = styled('nav', {
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  scrollbarWidth: 'none' /* Firefox */,
  '&::-webkit-scrollbar': {
    display: 'none',
    width: '0px',
    background: 'transparent' /* make scrollbar transparent */,
  },
});

const BaseNavList = styled('ul', {
  display: 'flex',
  gap: '$half',
  margin: 0,
  padding: 0,

  variants: {
    tabDistribution: {
      distributeEvenly: {
        justifyContent: 'stretch',
        '> .GDS-nav-item': {
          flex: '1 0 auto',
        },
      },
      hugContents: {
        justifyContent: 'flex-start',
      },
    },
  },
});

export const List = forwardRef<HTMLUListElement, GDSNavListProps>(
  (
    {
      children,
      className,
      css,
      divider,
      tabDistribution = 'distributeEvenly',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    },
    ref: React.Ref<HTMLUListElement>,
  ) => {
    const responsiveTabDistribution = useResponsiveProp(tabDistribution);
    const responsiveDivider = useResponsiveProp(divider);
    const {
      scrollBack,
      scrollForward,
      tabsList,
      showScrollBack,
      showScrollForward,
    } = useTabsScroll();
    const composedRefs = useComposedRefs<HTMLUListElement>(
      ref,
      tabsList as React.Ref<HTMLUListElement>,
    );
    const tabListWrapperRef = React.useRef(null);
    const scrollBackButtonRef = React.useRef(null);
    const scrollForwardButtonRef = React.useRef(null);

    const tabListElements = {
      tabListWrapper: tabListWrapperRef.current,
      scrollBackButton: scrollBackButtonRef.current,
      scrollForwardButton: scrollForwardButtonRef.current,
    };

    return (
      <TabListContextProvider tabListElements={tabListElements}>
        <GenesisCoreInspector displayName={DISPLAY_NAME}>
          <ScrollableWrapper data-testid={testId}>
            {showScrollBack && (
              <ScrollBack
                ref={scrollBackButtonRef}
                onClick={() => scrollBack()}
              />
            )}
            <ScrollableItemsWrapper
              ref={tabListWrapperRef}
              className="GDS-nav-list-wrapper"
            >
              <BaseNav
                className="GDS-nav"
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                ref={composedRefs}
              >
                <BaseNavList
                  className={[`GDS-nav-list`, className].join(' ')}
                  tabDistribution={responsiveTabDistribution}
                  tabIndex={0}
                  css={{
                    ...css,
                    ...(responsiveDivider && {
                      borderBlockEnd:
                        '$borderWidths$thin solid $onSurfaceBorderSubdued',
                    }),
                  }}
                  {...props}
                >
                  {children}
                </BaseNavList>
              </BaseNav>
            </ScrollableItemsWrapper>
            {showScrollForward && (
              <ScrollForward
                ref={scrollForwardButtonRef}
                onClick={() => scrollForward()}
              />
            )}
          </ScrollableWrapper>
        </GenesisCoreInspector>
      </TabListContextProvider>
    );
  },
);

export const NavList = {
  List,
  Link,
  RouterLink,
};

NavList.List.displayName = 'NavList.List';
NavList.Link.displayName = 'NavList.Link';
NavList.RouterLink.displayName = 'NavList.RouterLink';
